import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { RefreshCw, Package, ShoppingCart, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const DataMaintenance = () => {
  // BOM Sync State
  const [bomSync, setBomSync] = useState({
    status: 'idle', // idle, running, completed, error
    total: 0,
    processed: 0,
    synced: 0,
    errors: 0,
    currentSku: '',
    lastRun: null
  });
  
  // Order Re-enrich State
  const [orderEnrich, setOrderEnrich] = useState({
    status: 'idle', // idle, running, completed, error
    total: 0,
    processed: 0,
    updated: 0,
    alreadyCorrect: 0,
    errors: 0,
    currentOrder: '',
    lastRun: null
  });

  // Track if we've shown completion toast
  const bomCompletedRef = useRef(false);
  const orderCompletedRef = useRef(false);
  
  // Refs for polling intervals
  const bomPollRef = useRef(null);
  const orderPollRef = useRef(null);

  // Poll BOM sync progress
  useEffect(() => {
    const pollBom = async () => {
      try {
        const response = await fetch(`${API_URL}/api/v1/products/batch-sync-progress`, {
          headers: { 'X-Tenant-ID': 'tenant-123' }
        });
        const data = await response.json();
        
        if (data.success && data.progress) {
          const p = data.progress;
          
          if (p.is_running) {
            setBomSync({
              status: 'running',
              total: p.total || 0,
              processed: p.processed || 0,
              synced: p.synced || 0,
              errors: p.errors || 0,
              currentSku: p.current_sku || '',
              lastRun: null
            });
          } else if (p.completed_at) {
            setBomSync({
              status: 'completed',
              total: p.total || 0,
              processed: p.processed || 0,
              synced: p.synced || 0,
              errors: p.errors || 0,
              currentSku: '',
              lastRun: p.completed_at
            });
            // Show completion toast only once
            if (!bomCompletedRef.current && p.synced > 0) {
              bomCompletedRef.current = true;
              toast.success(`BOM Sync Complete: ${p.synced} products synced${p.errors > 0 ? `, ${p.errors} errors` : ''}`);
            }
          }
        }
      } catch (error) {
        console.error('Error polling BOM progress:', error);
      }
    };

    // Initial poll
    pollBom();
    
    // Set up interval polling when running
    if (bomSync.status === 'running') {
      bomPollRef.current = setInterval(pollBom, 1000);
    }
    
    return () => {
      if (bomPollRef.current) {
        clearInterval(bomPollRef.current);
      }
    };
  }, [bomSync.status]);

  // Poll Order re-enrich progress
  useEffect(() => {
    const pollOrder = async () => {
      try {
        const response = await fetch(`${API_URL}/api/v1/veeqo/re-enrich-progress`, {
          headers: { 'X-Tenant-ID': 'tenant-123' }
        });
        const data = await response.json();
        
        if (data.success && data.progress) {
          const p = data.progress;
          
          if (p.is_running) {
            setOrderEnrich({
              status: 'running',
              total: p.total || 0,
              processed: p.processed || 0,
              updated: p.updated || 0,
              alreadyCorrect: p.already_correct || 0,
              errors: p.errors || 0,
              currentOrder: p.current_order || '',
              lastRun: null
            });
          } else if (p.completed_at) {
            setOrderEnrich({
              status: 'completed',
              total: p.total || 0,
              processed: p.processed || 0,
              updated: p.updated || 0,
              alreadyCorrect: p.already_correct || 0,
              errors: p.errors || 0,
              currentOrder: '',
              lastRun: p.completed_at
            });
            // Show completion toast only once
            if (!orderCompletedRef.current && (p.updated > 0 || p.already_correct > 0)) {
              orderCompletedRef.current = true;
              toast.success(`Re-enrich Complete: ${p.updated} orders updated, ${p.already_correct} already correct`);
            }
          }
        }
      } catch (error) {
        console.error('Error polling order progress:', error);
      }
    };

    // Initial poll
    pollOrder();
    
    // Set up interval polling when running
    if (orderEnrich.status === 'running') {
      orderPollRef.current = setInterval(pollOrder, 1000);
    }
    
    return () => {
      if (orderPollRef.current) {
        clearInterval(orderPollRef.current);
      }
    };
  }, [orderEnrich.status]);

  // Start BOM Sync
  const startBomSync = async () => {
    try {
      // Reset completion tracking
      bomCompletedRef.current = false;
      
      // Set to running immediately for instant feedback
      setBomSync(prev => ({ ...prev, status: 'running', processed: 0, synced: 0, errors: 0 }));
      
      const response = await fetch(`${API_URL}/api/v1/products/batch-sync-bom-products`, {
        method: 'POST',
        headers: { 
          'X-Tenant-ID': 'tenant-123',
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (!data.success) {
        toast.error(data.message || 'Failed to start BOM sync');
        setBomSync(prev => ({ ...prev, status: 'error' }));
      }
    } catch (error) {
      console.error('Error starting BOM sync:', error);
      toast.error('Failed to start BOM sync');
      setBomSync(prev => ({ ...prev, status: 'error' }));
    }
  };

  // Start Order Re-enrich
  const startOrderReenrich = async () => {
    try {
      // Reset completion tracking
      orderCompletedRef.current = false;
      
      // Set to running immediately for instant feedback
      setOrderEnrich(prev => ({ ...prev, status: 'running', processed: 0, updated: 0, alreadyCorrect: 0, errors: 0 }));
      
      const response = await fetch(`${API_URL}/api/v1/veeqo/re-enrich-cogs`, {
        method: 'POST',
        headers: { 
          'X-Tenant-ID': 'tenant-123',
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (!data.success) {
        toast.error(data.message || 'Failed to start order re-enrichment');
        setOrderEnrich(prev => ({ ...prev, status: 'error' }));
      }
    } catch (error) {
      console.error('Error starting order re-enrich:', error);
      toast.error('Failed to start order re-enrichment');
      setOrderEnrich(prev => ({ ...prev, status: 'error' }));
    }
  };

  // Calculate progress percentages
  const bomProgressPct = bomSync.total > 0 ? Math.round((bomSync.processed / bomSync.total) * 100) : 0;
  const orderProgressPct = orderEnrich.total > 0 ? Math.round((orderEnrich.processed / orderEnrich.total) * 100) : 0;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="mb-4">
        <h2 className="text-base font-semibold text-white">Data Maintenance</h2>
        <p className="text-xs text-gray-400">
          Fix historical COGS data by syncing BOM products and re-enriching orders
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* BOM Sync Card */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <Package className="w-4 h-4 text-blue-400" />
              <CardTitle className="text-sm text-white">Sync All BOM Products</CardTitle>
            </div>
            <CardDescription className="text-xs text-gray-400">
              Recalculates COGS for all products with BOM and syncs pricing_v2.total_cost
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Running State */}
            {bomSync.status === 'running' && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm text-blue-400">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="font-medium">
                    Syncing... {bomSync.processed}/{bomSync.total} products
                  </span>
                </div>
                <Progress value={bomProgressPct} className="h-2" />
                <div className="flex items-center justify-between text-xs text-gray-400">
                  <span>{bomSync.currentSku ? `Processing: ${bomSync.currentSku}` : 'Starting...'}</span>
                  <span>{bomProgressPct}%</span>
                </div>
                {bomSync.synced > 0 && (
                  <div className="text-xs text-green-400">✓ {bomSync.synced} synced so far</div>
                )}
              </div>
            )}

            {/* Completed State */}
            {bomSync.status === 'completed' && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm text-green-400">
                  <CheckCircle2 className="w-4 h-4" />
                  <span className="font-medium">✓ Synced {bomSync.synced} products</span>
                </div>
                {bomSync.errors > 0 && (
                  <div className="flex items-center gap-2 text-xs text-amber-400">
                    <AlertCircle className="w-3 h-3" />
                    <span>{bomSync.errors} errors occurred</span>
                  </div>
                )}
                <Button 
                  onClick={startBomSync}
                  size="sm"
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  data-testid="bom-sync-btn"
                >
                  <RefreshCw className="w-3 h-3 mr-2" />
                  Run Again
                </Button>
              </div>
            )}

            {/* Idle State */}
            {(bomSync.status === 'idle' || bomSync.status === 'error') && (
              <Button 
                onClick={startBomSync}
                size="sm"
                className="w-full bg-blue-600 hover:bg-blue-700"
                data-testid="bom-sync-btn"
              >
                <RefreshCw className="w-3 h-3 mr-2" />
                Sync All BOM Products
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Order Re-enrich Card */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <ShoppingCart className="w-4 h-4 text-purple-400" />
              <CardTitle className="text-sm text-white">Re-enrich All Orders</CardTitle>
            </div>
            <CardDescription className="text-xs text-gray-400">
              Updates COGS on all existing orders using current Product Master costs
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Running State */}
            {orderEnrich.status === 'running' && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm text-purple-400">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="font-medium">
                    Re-enriching... {orderEnrich.processed.toLocaleString()}/{orderEnrich.total.toLocaleString()} orders ({orderProgressPct}%)
                  </span>
                </div>
                <Progress value={orderProgressPct} className="h-2" />
                <div className="flex items-center justify-between text-xs text-gray-400">
                  <span>{orderEnrich.currentOrder ? `Processing: ${orderEnrich.currentOrder}` : 'Starting...'}</span>
                  <span>~{Math.ceil((orderEnrich.total - orderEnrich.processed) / 17)} sec remaining</span>
                </div>
                <div className="flex items-center gap-4 text-xs">
                  <span className="text-green-400">✓ Updated: {orderEnrich.updated}</span>
                  <span className="text-gray-400">Already correct: {orderEnrich.alreadyCorrect}</span>
                </div>
              </div>
            )}

            {/* Completed State */}
            {orderEnrich.status === 'completed' && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm text-green-400">
                  <CheckCircle2 className="w-4 h-4" />
                  <span className="font-medium">✓ Re-enriched {orderEnrich.processed.toLocaleString()} orders</span>
                </div>
                <div className="text-xs text-gray-300 space-y-1">
                  <div>• {orderEnrich.updated} orders updated with new COGS</div>
                  <div>• {orderEnrich.alreadyCorrect} already had correct COGS</div>
                </div>
                {orderEnrich.errors > 0 && (
                  <div className="flex items-center gap-2 text-xs text-amber-400">
                    <AlertCircle className="w-3 h-3" />
                    <span>{orderEnrich.errors} errors occurred</span>
                  </div>
                )}
                <Button 
                  onClick={startOrderReenrich}
                  size="sm"
                  className="w-full bg-purple-600 hover:bg-purple-700"
                  data-testid="order-reenrich-btn"
                >
                  <RefreshCw className="w-3 h-3 mr-2" />
                  Run Again
                </Button>
              </div>
            )}

            {/* Idle State */}
            {(orderEnrich.status === 'idle' || orderEnrich.status === 'error') && (
              <Button 
                onClick={startOrderReenrich}
                size="sm"
                className="w-full bg-purple-600 hover:bg-purple-700"
                data-testid="order-reenrich-btn"
              >
                <RefreshCw className="w-3 h-3 mr-2" />
                Re-enrich All Orders
              </Button>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Info Box */}
      <Card className="bg-amber-900/20 border-amber-700/50">
        <CardContent className="p-3">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-amber-200">
              <p className="font-medium mb-1">Recommended Order of Operations:</p>
              <ol className="list-decimal list-inside space-y-0.5 text-amber-300/80">
                <li><strong>First:</strong> Run &ldquo;Sync All BOM Products&rdquo; to ensure pricing_v2.total_cost is correct</li>
                <li><strong>Then:</strong> Run &ldquo;Re-enrich All Orders&rdquo; to update historical orders with correct COGS</li>
                <li>After both complete, all profit calculations will use correct COGS values</li>
              </ol>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DataMaintenance;
